import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  FiPlay,
  FiCheckCircle,
  FiGlobe,
  FiShield,
  FiTerminal,
  FiTarget,
} from 'react-icons/fi';
import './adslot-flows.css';
import { useSelector } from 'react-redux';
import { pick } from 'lodash';
import { FlowHeader } from './components/layout/FlowHeaders';
import { FlowTypeSelector } from './components/sidebar/FlowTypeSelector';
import { FlowList } from './components/sidebar/FlowList';
import { FlowEditor } from './components/editor/FlowEditor';
import { RootState } from 'types';
import { Flow, FlowType, FlowTypeInfo } from '../../../types/flow';
import { toast } from 'react-toastify';
import { track } from 'analytics/posthog';
import useHeader from 'containers/App/Header/useHeader';

// API hooks
import {
  useGetTvCommandsQuery,
  useCreateTvCommandMutation,
  useUpdateTvCommandMutation,
  useDeleteTvCommandMutation,
} from 'services/api/tvCommands';

// Mapper
import {
  dbToUiFlow,
  uiToDbPayload,
  isMongoObjectId,
} from '../../../types/tvCommandTypeMapper';

// Helpers to normalize filter arrays into consistent strings for comparison (prevent duplication)
const normalizeOne = (arr?: string[]) =>
  (arr ?? []).filter(Boolean).slice(0, 1)[0] ?? '';

const normalizeMany = (arr?: string[]) =>
  (arr ?? [])
    .filter(Boolean)
    .map(s => s.trim())
    .filter(Boolean)
    .sort()
    .join('|');

function adslotFilterKey(flow: Flow): string {
  if (flow.type !== 'adslot') return '';

  const f = flow.filters;
  return [
    `adSlot=${normalizeOne(f?.adSlots)}`,
    `make=${normalizeOne(f?.makes)}`,
    `os=${normalizeMany(f?.osVersions)}`,
    `adTypes=${normalizeMany(f?.adTypes)}`,
    `destTypes=${normalizeMany(f?.destTypes)}`,
    `countries=${normalizeMany(f?.countries)}`,
  ].join('::');
}

function getFlowAnalyticsProps(flow: Flow) {
  return {
    flow_type: flow.type,
    adslot: flow.filters?.adSlots?.[0],
  };
}

const AdSlotFlows: React.FC = () => {
  useHeader('AdSlot Flows');

  type ThemeMode = 'dark' | 'light';

  const [selectedType, setSelectedType] = useState<FlowType>('pre');
  const [selectedFlow, setSelectedFlow] = useState<Flow | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [originalFlow, setOriginalFlow] = useState<Flow | null>(null);
  const [theme, setTheme] = useState<ThemeMode>(() => {
    const stored = window.localStorage.getItem('adslot-theme');
    return (stored === 'light' || stored === 'dark') ? (stored as ThemeMode) : 'dark';
  });

  type AdslotSearchFilters = {
    adSlot?: string; // single
    make?: string; // single
    osVersions: string[]; // multi
    adTypes: string[]; // multi
    destTypes: string[]; // multi
    countries: string[]; // multi
  };

  const EMPTY_ADSLOT_SEARCH_FILTERS: AdslotSearchFilters = {
    adSlot: undefined,
    make: undefined,
    osVersions: [],
    adTypes: [],
    destTypes: [],
    countries: [],
  };

  const [adslotSearchFilters, setAdslotSearchFilters] =
    useState<AdslotSearchFilters>(EMPTY_ADSLOT_SEARCH_FILTERS);

  // Fetch ALL flows (we filter client side)
  const {
    data: dbDocs = [],
    isLoading,
    error,
  } = useGetTvCommandsQuery(undefined);

  const [createTvCommand] = useCreateTvCommandMutation();
  const [updateTvCommand] = useUpdateTvCommandMutation();
  const [deleteTvCommand] = useDeleteTvCommandMutation();

  // Convert DB format to UI flows
  const flows: Flow[] = useMemo(() => dbDocs.map(dbToUiFlow), [dbDocs]);

  const flowsWithSelected = useMemo(() => {
    if (!selectedFlow) return flows;

    // If selectedFlow isn't in the server list yet (newly created), include it so UI feels consistent
    const exists = flows.some(f => f.id === selectedFlow.id);
    if (!exists) return [selectedFlow, ...flows];

    return flows;
  }, [flows, selectedFlow]);

  const flowTypeInfos: FlowTypeInfo[] = useMemo(
    () => [
      {
        type: 'pre',
        label: 'Pre Steps',
        icon: <FiPlay className="adslot-icon-inline" />,
        count: flowsWithSelected.filter(f => f.type === 'pre').length,
      },
      {
        type: 'post',
        label: 'Post Steps',
        icon: <FiCheckCircle className="adslot-icon-inline" />,
        count: flowsWithSelected.filter(f => f.type === 'post').length,
      },
      {
        type: 'country',
        label: 'Country Change',
        icon: <FiGlobe className="adslot-icon-inline" />,
        count: flowsWithSelected.filter(f => f.type === 'country').length,
      },
      {
        type: 'vpn',
        label: 'VPN Change',
        icon: <FiShield className="adslot-icon-inline" />,
        count: flowsWithSelected.filter(f => f.type === 'vpn').length,
      },
      {
        type: 'base',
        label: 'Base Commands',
        icon: <FiTerminal className="adslot-icon-inline" />,
        count: flowsWithSelected.filter(f => f.type === 'base').length,
      },
      {
        type: 'adslot',
        label: 'AdSlot Flow',
        icon: <FiTarget className="adslot-icon-inline" />,
        count: flowsWithSelected.filter(f => f.type === 'adslot').length,
      },
    ],
    [flowsWithSelected],
  );

  const filteredFlows = useMemo(
    () => flowsWithSelected.filter(flow => flow.type === selectedType),
    [flowsWithSelected, selectedType],
  );

  // Initialize selection once data arrives (only if nothing selected yet)
  const hasToastedLoadErrorRef = React.useRef(false);

  const user = pick(
    useSelector((state: RootState) => state.session.user),
    ['email', 'name'],
  );

  const updatedByFallback = user?.email || user?.name || 'system';
  
  useEffect(() => {
    window.localStorage.setItem('adslot-theme', theme);
  }, [theme]);

  useEffect(() => {
    if (isLoading) return;

    if (error) {
      // Prevent spamming toasts
      if (!hasToastedLoadErrorRef.current) {
        toast.error('Failed to load TV commands');
        hasToastedLoadErrorRef.current = true;
      }
      return;
    }

    hasToastedLoadErrorRef.current = false;

    if (!selectedFlow) {
      const first = flows.find(f => f.type === selectedType) || null;
      setSelectedFlow(first);
      setOriginalFlow(first);
      setHasUnsavedChanges(false);
    }
  }, [isLoading, error, flows, selectedType, selectedFlow]);

  // Keep selectedFlow in sync with latest refetched flows
  useEffect(() => {
    if (!selectedFlow) return;
    if (!isMongoObjectId(selectedFlow.id)) return;
    if (hasUnsavedChanges) return;

    const refreshed = flows.find(f => f.id === selectedFlow.id);
    if (refreshed) {
      setSelectedFlow(refreshed);
      setOriginalFlow(refreshed);
    }
  }, [flows, selectedFlow, hasUnsavedChanges]);

  const toggleTheme = () => setTheme(t => (t === 'dark' ? 'light' : 'dark'));

  const handleSelectType = (type: FlowType) => {
    setSelectedType(type);
    setSearchQuery('');

    if (type !== 'adslot') {
      setAdslotSearchFilters(EMPTY_ADSLOT_SEARCH_FILTERS);
    }

    const firstFlow = flowsWithSelected.find(f => f.type === type);
    if (firstFlow) {
      setSelectedFlow(firstFlow);
      setOriginalFlow(firstFlow);
      setHasUnsavedChanges(false);
    } else {
      setSelectedFlow(null);
      setOriginalFlow(null);
    }
  };

  const handleSelectFlow = (flow: Flow) => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm(
        'You have unsaved changes. Are you sure you want to switch flows?',
      );
      if (!confirmed) return;
    }
    setSelectedFlow(flow);
    setOriginalFlow(flow);
    setHasUnsavedChanges(false);
  };

  const handleCreateNew = () => {
    const newFlow: Flow = {
      id: `new-${Date.now()}`, // temp id until saved
      key: selectedType === 'base' ? '' : undefined,
      name: '',
      type: selectedType,
      description: '',
      commands: [],
      updatedAt: new Date().toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      }),
      updatedBy: updatedByFallback,
      filters:
        selectedType === 'adslot' || selectedType === 'pre'
          ? {
              adSlots: [],
              osVersions: [],
              makes: [],
              adTypes: [],
              destTypes: [],
              countries: [],
            }
          : undefined,
      tags: [],
      isDefault: false,
    };

    setSelectedFlow(newFlow);
    setOriginalFlow(null);
    setHasUnsavedChanges(true);
  };

  const handleFlowChange = useCallback((updatedFlow: Flow) => {
    setSelectedFlow(updatedFlow);
    setHasUnsavedChanges(true);
  }, []);

  const handleSave = async (flow: Flow) => {
    try {
      // Prevent creating/updating flow with duplicate name
      const incomingName = (flow.name ?? '').trim().toLowerCase();
      if (incomingName) {
        const same_name = flows.find(
          f =>
            f.type === flow.type &&
            f.id !== flow.id &&
            (f.name ?? '').trim().toLowerCase() === incomingName,
        );

        if (same_name) {
          toast.error(`A flow with the name "${flow.name}" already exists.`);
          return;
        }
      }

      // Prevent creating/updating adslot flow without necessary filters
      if (flow.type === 'adslot') {
        const f = flow.filters;
        const hasAtLeastOne = (arr?: string[]) => (arr ?? []).filter(Boolean).length > 0;

        if (!f || !hasAtLeastOne(f.adSlots) || !hasAtLeastOne(f.makes) ||
            !hasAtLeastOne(f.osVersions) || !hasAtLeastOne(f.adTypes) ||
            !hasAtLeastOne(f.destTypes) || !hasAtLeastOne(f.countries)) {
          toast.error('AdSlot flows must have at least one of each filter selected');
          return;
        }

        // Prevent creating/updating duplicate adslot flows with same filters
        const key = adslotFilterKey(flow);

        // compare against other saved flows
        const existing = flows.find(
          f => f.type === 'adslot' && f.id !== flow.id && adslotFilterKey(f) === key,
        );

        if (existing) {
          toast.info(
            `A flow already exists for these filters: "${existing.name || 'Untitled'}". Opening it instead.`,
          );
          setSelectedType(existing.type);
          setSelectedFlow(existing);
          setOriginalFlow(existing);
          setHasUnsavedChanges(false);
          return;
        }
      }

      if (flow.type === 'pre') {
        const hasOsVersion = (flow.filters?.osVersions ?? []).filter(Boolean).length > 0;
        if (!hasOsVersion) {
          toast.error('Pre step flows must have at least one OS version selected');
          return;
        }
      }

      if (flow.type === 'base') {
        if (flow.commands.length !== 1 || flow.commands[0].type !== 'command') {
          toast.error('Base command scripts must contain exactly one command');
          return;
        }
        if (!flow.commands[0].command?.trim()) {
          toast.error('Base command cannot be empty');
          return;
        }
      }

      const payload = uiToDbPayload(flow);

      payload.updatedBy = updatedByFallback;
      if (flow.type === 'base') {
        payload.key = (flow.key || flow.name || '').trim();
      }

      if (isMongoObjectId(flow.id)) {
        const updated = await updateTvCommand({
          id: flow.id,
          body: payload,
        }).unwrap();

        const mapped = dbToUiFlow(updated);
        track('adslot flow updated', getFlowAnalyticsProps(mapped));
        setSelectedFlow(mapped);
        setOriginalFlow(mapped);
        setHasUnsavedChanges(false);
        toast.success('Flow saved');
      } else {
        const created = await createTvCommand(payload).unwrap();

        const mapped = dbToUiFlow(created);
        track('adslot flow updated', getFlowAnalyticsProps(mapped));
        setSelectedFlow(mapped);
        setOriginalFlow(mapped);
        setHasUnsavedChanges(false);
        toast.success('Flow created');
      }
    } catch (e: any) {
      toast.error(e?.data?.error || e?.message || 'Failed to save flow');
    }
  };

  const handleDiscard = () => {
    // Discarding a new flow
    if (selectedFlow && !isMongoObjectId(selectedFlow.id)) {
      const first = flows.find(f => f.type === selectedType) || null;
      setSelectedFlow(first);
      setOriginalFlow(first);
      setHasUnsavedChanges(false);
      toast.info('Changes discarded');
      return;
    }

    // Discarding changes to a saved flow:
    if (originalFlow) {
      setSelectedFlow(originalFlow);
    } else {
      const first = flows.find(f => f.type === selectedType) || null;
      setSelectedFlow(first);
    }

    setHasUnsavedChanges(false);
    toast.info('Changes discarded');
  };

  const handleDeleteFlow = async (id: string) => {
    try {
      const flowToDelete = flows.find(f => f.id === id);
      await deleteTvCommand(id).unwrap();

      if (flowToDelete) {
        track('adslot flow updated', getFlowAnalyticsProps(flowToDelete));
      }
      toast.success('Flow deleted');

      // Immediately update selection without waiting for refetch:
      const remaining = flows.filter(f => f.id !== id);
      const next = remaining.find(f => f.type === selectedType) || null;

      setSelectedFlow(next);
      setOriginalFlow(next);
      setHasUnsavedChanges(false);
    } catch (e: any) {
      toast.error(e?.data?.error || e?.message || 'Failed to delete flow');
    }
  };

  return (
    <div className="adslot-page" data-theme={theme}>
      <section className="adslot-admin-header">
        <div className="adslot-admin-eyebrow">Admin Center</div>
        <h1 className="adslot-admin-title">AdSlot Flows</h1>
        <p className="adslot-admin-subtitle">
          Maintain the command flows used by AdSure creative testing.
        </p>
      </section>

      <FlowHeader
        isSaved={!hasUnsavedChanges}
        lastUpdated={selectedFlow?.updatedAt}
        lastUpdatedBy={selectedFlow?.updatedBy}
        theme={theme}
        onToggleTheme={toggleTheme}
      />

      <div className="adslot-main">
        {/* Sidebar */}
        <aside className="adslot-sidebar">
          <FlowTypeSelector
            flowTypes={flowTypeInfos}
            selectedType={selectedType}
            onSelectType={handleSelectType}
          />
          <div className="adslot-sidebar-bottom">
            <FlowList
              flows={filteredFlows}
              selectedFlowId={selectedFlow?.id || null}
              flowType={selectedType}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              onSelectFlow={handleSelectFlow}
              onCreateNew={handleCreateNew}
              adslotSearchFilters={adslotSearchFilters}
              onAdslotSearchFiltersChange={setAdslotSearchFilters}
            />
          </div>
        </aside>

        {/* Main Editor */}
        <main className="adslot-editor-main">
          <FlowEditor
            flow={selectedFlow}
            flowType={selectedType}
            hasUnsavedChanges={hasUnsavedChanges}
            onSave={handleSave}
            onDiscard={handleDiscard}
            onFlowChange={handleFlowChange}
            onDelete={handleDeleteFlow}
          />
        </main>
      </div>
    </div>
  );
};

export default AdSlotFlows;
